<template>
  <v-container fluid class="pa-6">
    <v-row>
      <v-col cols="12">
        <!-- Header with Back Button -->
        <div class="d-flex align-center mb-6" style="gap: 8px">
          <v-btn
            variant="text"
            size="small"
            prepend-icon="mdi-arrow-left"
            class="text-grey-darken-2"
            @click="goBack"
          >
            Back
          </v-btn>
          <h2 class="text-h5 font-weight-medium" style="margin: 0">Create Company User</h2>
        </div>

        <!-- Success Dialog -->
        <v-dialog v-model="showSuccess" max-width="500" persistent data-testid="success-dialog">
          <v-card>
            <v-card-title class="text-h6 bg-primary text-white pa-4">
              <v-icon icon="mdi-check-circle" class="mr-2"></v-icon>
              User Created Successfully!
            </v-card-title>
            <v-card-text class="pa-6">
              <p class="mb-2">An invitation email with a temporary password has been sent to:</p>
              <p class="font-weight-bold mb-0" data-testid="success-email">{{ form.email }}</p>
            </v-card-text>
            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn
                data-testid="success-ok-btn"
                color="primary"
                variant="flat"
                @click="handleSuccessOk"
              >
                OK
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Form Card -->
        <v-card class="mx-auto" max-width="800">
          <v-card-text class="pa-6">
            <v-form ref="formRef" @submit.prevent="handleSubmit">
              <!-- Username -->
              <v-text-field
                data-testid="username-input"
                v-model="form.username"
                label="Username *"
                placeholder="Enter username"
                :rules="[rules.required, rules.username]"
                prepend-inner-icon="mdi-account"
                variant="outlined"
                density="comfortable"
                class="mb-4"
                hint="No spaces, lowercase letters, numbers, and underscores only"
                persistent-hint
              ></v-text-field>

              <!-- Email -->
              <v-text-field
                data-testid="email-input"
                v-model="form.email"
                label="Email *"
                placeholder="Enter email address"
                type="email"
                :rules="[rules.required, rules.email]"
                prepend-inner-icon="mdi-email"
                variant="outlined"
                density="comfortable"
                class="mb-4"
              ></v-text-field>

              <!-- Role Selection -->
              <v-select
                data-testid="role-select"
                v-model="form.role"
                :items="allowedRoleOptions"
                item-title="title"
                item-value="value"
                label="Role *"
                placeholder="Select a role"
                :rules="[rules.required]"
                prepend-inner-icon="mdi-shield-account"
                variant="outlined"
                density="comfortable"
                class="mb-6"
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item
                    v-bind="props"
                    :title="item.raw.title"
                    :subtitle="item.raw.description"
                  ></v-list-item>
                </template>
              </v-select>

              <!-- Info Alert -->
              <v-alert type="info" variant="tonal" density="compact" class="mb-6">
                <div class="text-body-2">
                  <strong>What happens next:</strong>
                  <ul class="mt-2 ml-4">
                    <li>User will receive an invitation email with a temporary password</li>
                    <li>They'll be prompted to set a new password on first login</li>
                    <li>Email is automatically verified</li>
                  </ul>
                </div>
              </v-alert>

              <!-- Action Buttons -->
              <div class="d-flex justify-end" style="gap: 12px">
                <v-btn data-testid="cancel-btn" variant="text" @click="goBack" :disabled="loading">
                  Cancel
                </v-btn>
                <v-btn
                  data-testid="create-user-btn"
                  type="submit"
                  color="primary"
                  variant="flat"
                  :loading="loading"
                  :disabled="!isFormValid || loading"
                  prepend-icon="mdi-account-plus"
                >
                  Create User
                </v-btn>
              </div>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Snackbar for errors -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="5000">
      {{ snackbarMessage }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { createUserSimple } from '@/api/accountsAdmin'
import { useUserStore } from '@/stores/userStore'

const router = useRouter()
const userStore = useUserStore()

// Form state
const formRef = ref()
const loading = ref(false)
const showSuccess = ref(false)
const snackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('error')

// Form data
const form = ref({
  username: '',
  email: '',
  role: '',
})

// Check if form is valid
const isFormValid = computed(() => {
  // Check if all required fields are filled
  if (!form.value.username || !form.value.email || !form.value.role) {
    return false
  }

  // Check username format
  if (!/^[a-z0-9_]+$/.test(form.value.username)) {
    return false
  }

  // Check email format
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
    return false
  }

  // Check email domain
  if (!form.value.email.endsWith('@jazel.com') && !form.value.email.endsWith('@10thdegree.com')) {
    return false
  }

  return true
})

// Company role options
const roleOptions = [
  { title: 'Company User', value: 'company_user', description: 'Basic company access' },
  {
    title: 'Company Admin',
    value: 'company_admin',
    description: 'Can manage accounts and settings',
  },
  {
    title: 'Company Super Admin',
    value: 'company_suadmin',
    description: 'Advanced administrative access',
  },
  { title: 'Company Owner', value: 'company_owner', description: 'Full system access' },
]

// Only allow creating same or lower role than current user
const roleHierarchy = ['company_user', 'company_admin', 'company_suadmin', 'company_owner']
const allowedRoleOptions = computed(() => {
  const myRole = String(userStore?.role || 'company_user')
  const myIndex = roleHierarchy.indexOf(myRole)
  if (myIndex < 0) return roleOptions.filter((o) => o.value === 'company_user')
  return roleOptions.filter((opt) => roleHierarchy.indexOf(opt.value) <= myIndex)
})

// Validation rules
const rules = {
  required: (v: any) => !!v || 'This field is required',
  email: (v: string) => {
    if (!v) return 'Email is required'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Invalid email address'
    if (!v.endsWith('@jazel.com') && !v.endsWith('@10thdegree.com')) return 'Email must be a @jazel.com or @10thdegree.com address'
    return true
  },
  username: (v: string) =>
    /^[a-z0-9_]+$/.test(v) || 'Username must be lowercase letters, numbers, and underscores only',
}

// Handle form submission
async function handleSubmit() {
  const { valid } = await formRef.value.validate()
  if (!valid) return

  try {
    // Guard: Ensure selected role is within allowed roles for current user
    const allowed = allowedRoleOptions.value.some((o) => o.value === form.value.role)
    if (!allowed) {
      snackbarMessage.value = 'You can only create users with your role or lower.'
      snackbarColor.value = 'error'
      snackbar.value = true
      return
    }
    loading.value = true
    await createUserSimple(form.value.username, form.value.email, form.value.role, undefined)
    showSuccess.value = true
  } catch (error: any) {
    let errorMsg = 'Failed to create user. Please try again.'
    if (error?.response?.data?.message) errorMsg = error.response.data.message
    else if (error?.response?.data)
      errorMsg =
        typeof error.response.data === 'string'
          ? error.response.data
          : JSON.stringify(error.response.data)
    else if (error?.message) errorMsg = error.message
    snackbarMessage.value = errorMsg
    snackbarColor.value = 'error'
    snackbar.value = true
  } finally {
    loading.value = false
  }
}

function handleSuccessOk() {
  showSuccess.value = false
  // Add query param to trigger refresh
  router.push({ path: '/company-accounts', query: { refresh: Date.now().toString() } })
}

function goBack() {
  router.back()
}
</script>

<style scoped>
::deep(.v-field) {
  border-radius: 8px;
}
::deep(.v-alert) {
  border-radius: 8px;
}
</style>
